import { useState, useEffect, useRef } from 'react';

interface WeekTrackerProps {
  selectedDate: string;  // ISO date YYYY-MM-DD
  onDateSelect: (date: string) => void;
  moveCounts: Record<string, number>;  // date -> count of moves
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekStart(isoDate: string): Date {
  const d = new Date(isoDate + 'T12:00:00');
  const dow = d.getDay(); // 0=Sun, 1=Mon, ...
  const diff = dow === 0 ? -6 : 1 - dow; // shift to Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return monday;
}

const DAY_ABBRS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function WeekTracker({ selectedDate, onDateSelect, moveCounts }: WeekTrackerProps) {
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(selectedDate));
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 50;

  // When selectedDate changes to a day outside current week, adjust week
  useEffect(() => {
    const ws = getWeekStart(selectedDate);
    if (toISO(ws) !== toISO(weekStart)) {
      setWeekStart(ws);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayISO = toISO(new Date());

  // Build 7 days of the current week
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const iso = toISO(d);
    return {
      iso,
      abbr: DAY_ABBRS[i],
      num: d.getDate(),
      isToday: iso === todayISO,
      count: moveCounts[iso] ?? 0,
    };
  });

  const goToPrevWeek = () => {
    const prev = new Date(weekStart);
    prev.setDate(weekStart.getDate() - 7);
    setWeekStart(prev);
    // Select the same day-of-week in the previous week
    const selectedDow = new Date(selectedDate + 'T12:00:00').getDay();
    const newDow = selectedDow === 0 ? 6 : selectedDow - 1;
    const newDate = new Date(prev);
    newDate.setDate(prev.getDate() + newDow);
    onDateSelect(toISO(newDate));
  };

  const goToNextWeek = () => {
    const next = new Date(weekStart);
    next.setDate(weekStart.getDate() + 7);
    setWeekStart(next);
    // Select the same day-of-week in the next week
    const selectedDow = new Date(selectedDate + 'T12:00:00').getDay();
    const newDow = selectedDow === 0 ? 6 : selectedDow - 1;
    const newDate = new Date(next);
    newDate.setDate(next.getDate() + newDow);
    onDateSelect(toISO(newDate));
  };

  // Touch swipe support
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) goToNextWeek();
      else goToPrevWeek();
    }
    touchStartX.current = null;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevWeek();
      else if (e.key === 'ArrowRight') goToNextWeek();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [weekStart, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      className="relative group flex items-center"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Left chevron (desktop hover) */}
      <button
        onClick={goToPrevWeek}
        className="absolute -left-4 opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-canvas-secondary transition-opacity hidden sm:flex"
        aria-label="Previous week"
      >
        <span className="material-icons-round text-ink-tertiary" style={{ fontSize: '18px' }}>chevron_left</span>
      </button>

      {/* Days */}
      <div className="flex justify-between items-center flex-1 touch-pan-y">
        {days.map((day) => {
          const isSelected = day.iso === selectedDate;
          return (
            <button
              key={day.iso}
              onClick={() => onDateSelect(day.iso)}
              className="flex flex-col items-center min-w-[36px] sm:min-w-[40px] transition-all active:scale-95"
              aria-label={`${day.abbr} ${day.num}${isSelected ? ' (selected)' : ''}${day.count > 0 ? ` (${day.count} moves)` : ''}`}
              aria-current={isSelected ? 'step' : undefined}
            >
              <span className="text-xs font-medium text-ink-tertiary mb-1">{day.abbr}</span>
              <span
                className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl text-sm sm:text-base font-semibold transition-all ${
                  isSelected ? 'bg-ink text-white' : day.isToday ? 'text-primary font-bold' : 'text-ink'
                }`}
              >
                {day.num}
              </span>
              <div className="h-2 flex items-center justify-center mt-1">
                {day.count > 0 && (
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      day.isToday ? 'bg-primary' : isSelected ? 'bg-white/70' : 'bg-ink-tertiary'
                    }`}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Right chevron (desktop hover) */}
      <button
        onClick={goToNextWeek}
        className="absolute -right-4 opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-canvas-secondary transition-opacity hidden sm:flex"
        aria-label="Next week"
      >
        <span className="material-icons-round text-ink-tertiary" style={{ fontSize: '18px' }}>chevron_right</span>
      </button>
    </div>
  );
}

export default WeekTracker;
